/* New Arrivals editor recovery. Keep the existing initial-page carousel;
 * rebuild only sections whose inline startup did not run after editor replacement.
 */
(() => {
  'use strict';
  if (window.__adornSectionRecoveryV1) return;
  window.__adornSectionRecoveryV1 = true;
  const states = new Map();
  const rootsIn = scope => {
    const roots = Array.from(scope.querySelectorAll('[data-na-section]'));
    if (scope instanceof Element && scope.matches('[data-na-section]')) roots.unshift(scope);
    return roots;
  };
  function init(root) {
    if (states.has(root)) return;
    const tabs = Array.from(root.querySelectorAll('[data-group-tab]'));
    const panels = Array.from(root.querySelectorAll('.na-panel'));
    const subtabs = root.querySelector('[data-subtabs]');
    const dock = root.querySelector('.na-main-tabs');
    const viewAll = root.querySelector('[data-view-all]');
    if (!tabs.length || !subtabs || !dock) return;
    const controller = new AbortController();
    const state = { controller, raf: 0, animationCleanup: null, observer: null, dead: false };
    const signal = controller.signal;
    states.set(root, state);
    const recovery = root.dataset.naBound !== 'true';
    const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fallback = root.querySelector('.na-new-fallback');
    if (fallback && !fallback.textContent.trim()) fallback.textContent = 'NEW';
    const lottie = root.querySelector('.na-new-lottie');
    if (lottie && !lottie.getAttribute('aria-label')) lottie.setAttribute('aria-label', 'NEW');
    function decorate() {
      if (!root.isConnected) return;
      const active = tabs.find(tab => tab.classList.contains('is-active')) || tabs[0];
      const extra = innerWidth < 750 ? 14 : 18;
      dock.style.setProperty('--dock-left', (active.offsetLeft - extra) + 'px');
      dock.style.setProperty('--dock-width', (active.offsetWidth + extra * 2) + 'px');
      tabs.forEach(tab => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', String(tab === active));
        tab.tabIndex = tab === active ? 0 : -1;
      });
      subtabs.setAttribute('role', 'tablist');
      subtabs.setAttribute('aria-label', 'New arrival collections');
      panels.forEach((panel, i) => {
        if (!panel.id) panel.id = root.id + '-panel-' + i;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-hidden', String(!panel.classList.contains('is-active')));
        const track = panel.querySelector('[data-products-track]');
        if (!track || !panel.classList.contains('is-active')) return;
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        const prev = panel.querySelector('[data-arrow-prev]');
        const next = panel.querySelector('[data-arrow-next]');
        if (prev) prev.disabled = track.scrollLeft <= 1 || max < 2;
        if (next) next.disabled = track.scrollLeft >= max - 1 || max < 2;
      });
      subtabs.querySelectorAll('.na-subtab').forEach((button, i) => {
        const panel = panels.find(item => item.dataset.handle === button.dataset.panelId);
        button.setAttribute('role', 'tab');
        button.tabIndex = button.classList.contains('is-active') ? 0 : -1;
        if (!button.id) button.id = root.id + '-subtab-' + i;
        if (panel) { button.setAttribute('aria-controls', panel.id); panel.setAttribute('aria-labelledby', button.id); }
      });
    }
    function schedule() {
      cancelAnimationFrame(state.raf);
      state.raf = requestAnimationFrame(decorate);
    }
    function activate(id) {
      panels.forEach(panel => panel.classList.toggle('is-active', panel.dataset.handle === id));
      subtabs.querySelectorAll('.na-subtab').forEach(button => {
        const active = button.dataset.panelId === id;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
      });
      const selected = panels.find(panel => panel.dataset.handle === id);
      if (viewAll) {
        viewAll.style.display = selected && selected.dataset.url ? '' : 'none';
        if (selected && selected.dataset.url) viewAll.href = selected.dataset.url;
      }
      schedule();
    }
    function group(tab) {
      tabs.forEach(item => item.classList.toggle('is-active', item === tab));
      subtabs.replaceChildren();
      const matching = panels.filter(panel => panel.dataset.group === tab.dataset.groupTab);
      matching.forEach(panel => {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'na-subtab';
        button.textContent = panel.dataset.title || 'Collection';
        button.dataset.panelId = panel.dataset.handle;
        button.addEventListener('click', () => activate(panel.dataset.handle), { signal });
        subtabs.appendChild(button);
      });
      activate(matching.length ? matching[0].dataset.handle : '');
    }
    if (recovery) {
      root.dataset.naBound = 'true';
      tabs.forEach(tab => tab.addEventListener('click', () => group(tab), { signal }));
      panels.forEach(panel => {
        const track = panel.querySelector('[data-products-track]');
        if (!track) return;
        for (const [selector, direction] of [['[data-arrow-prev]', -1], ['[data-arrow-next]', 1]]) {
          const button = panel.querySelector(selector);
          if (button) button.addEventListener('click', () => {
            const card = track.querySelector('.na-product-card');
            const step = (card ? card.getBoundingClientRect().width : 240) + (parseFloat(getComputedStyle(track).gap) || 0);
            track.scrollBy({ left: step * direction, behavior: reduced() ? 'auto' : 'smooth' });
          }, { signal });
        }
      });
      group(tabs[0]);
      if (lottie && lottie.dataset.lottieUrl && !reduced() && window.AdornAssets) {
        function startAnimation() {
          if (state.dead || reduced()) return;
          window.AdornAssets.loadLottie().then(api => {
            if (state.dead || !root.isConnected) return;
            const animation = api.loadAnimation({ container: lottie, renderer: 'svg', autoplay: false,
              loop: lottie.dataset.lottieLoop !== 'false', path: lottie.dataset.lottieUrl });
            const speed = Number(lottie.dataset.lottieSpeed);
            animation.setSpeed(Number.isFinite(speed) && speed > 0 ? speed : 1);
            state.animationCleanup = window.AdornAssets.watchAnimation(lottie, animation, lottie.dataset.lottieAutoplay !== 'false');
            animation.addEventListener('DOMLoaded', () => {
              if (state.dead) return;
              lottie.classList.add('is-ready');
              lottie.closest('.na-new-lottie-wrap')?.classList.add('is-lottie-ready');
            });
            animation.addEventListener('data_failed', () => {
              lottie.classList.remove('is-ready');
              lottie.closest('.na-new-lottie-wrap')?.classList.remove('is-lottie-ready');
              if (state.animationCleanup) { state.animationCleanup(); state.animationCleanup = null; }
            });
          }).catch(() => {});
        }
        if ('IntersectionObserver' in window) {
          state.observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) { state.observer.disconnect(); startAnimation(); }
          }, { rootMargin: '150px 0px' });
          state.observer.observe(root);
        } else startAnimation();
      }
    }
    root.addEventListener('click', schedule, { signal });
    root.querySelectorAll('[data-products-track]').forEach(track => track.addEventListener('scroll', schedule, { passive: true, signal }));
    root.addEventListener('keydown', event => {
      if (!(event.target instanceof Element) || !event.target.matches('[data-group-tab],.na-subtab')) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const list = event.target.matches('[data-group-tab]') ? tabs : Array.from(subtabs.querySelectorAll('.na-subtab'));
      const index = list.indexOf(event.target);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? list.length - 1 :
        (index + (event.key === 'ArrowRight' ? 1 : -1) + list.length) % list.length;
      event.preventDefault(); list[next].click(); list[next].focus({ preventScroll: true }); schedule();
    }, { signal });
    if (recovery) {
      function deliveryTags() {
        const engine = window.AdornDeliveryEngine;
        if (!engine) return;
        const pin = engine.getSelectedPin();
        root.querySelectorAll('[data-na-delivery-tag]').forEach(tag => {
          const estimate = pin ? engine.getEstimate(pin, tag.dataset.shipsIn || '') : null;
          const dates = estimate && engine.getDeliveryDates(estimate);
          const format = date => new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' }).format(date);
          const range = dates ? format(dates.minDate) + (dates.minDate.getTime() === dates.maxDate.getTime() ? '' : ' - ' + format(dates.maxDate)) : '';
          const text = tag.querySelector('[data-na-delivery-text]');
          const icon = tag.querySelector('[data-na-delivery-icon]');
          const label = pin ? (range ? 'FREE Delivery ' + range : 'Check Delivery') : 'FREE Delivery';
          if (text) text.textContent = label;
          if (icon) icon.textContent = estimate && estimate.type === 'mumbai' ? '⚡' : '🚚';
          tag.classList.toggle('is-mumbai-express', Boolean(estimate && estimate.type === 'mumbai'));
          tag.classList.toggle('is-unavailable', Boolean(pin && !estimate));
          tag.setAttribute('aria-label', label + (pin ? ' for pincode ' + pin : ' — check pincode'));
        });
      }
      window.addEventListener('adorn:pincode-selected', deliveryTags, { signal });
      deliveryTags();
    }
    state.schedule = schedule;
    decorate();
  }
  rootsIn(document).forEach(init);
  document.addEventListener('shopify:section:load', event => rootsIn(event.target).forEach(init));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, state] of states) {
      if (event.target === root || event.target.contains(root)) {
        state.dead = true; state.controller.abort(); cancelAnimationFrame(state.raf);
        if (state.observer) state.observer.disconnect();
        if (state.animationCleanup) state.animationCleanup();
        states.delete(root);
      }
    }
  });
  document.addEventListener('shopify:block:select', event => {
    if (!(event.target instanceof Element)) return;
    const panel = event.target.closest('.na-panel') || event.target.querySelector('.na-panel');
    const root = panel && panel.closest('[data-na-section]');
    if (!root) return;
    init(root);
    const main = Array.from(root.querySelectorAll('[data-group-tab]')).find(tab => tab.dataset.groupTab === panel.dataset.group);
    if (main) main.click();
    const sub = Array.from(root.querySelectorAll('.na-subtab')).find(tab => tab.dataset.panelId === panel.dataset.handle);
    if (sub) sub.click();
    const state = states.get(root); if (state) state.schedule();
  });
  window.addEventListener('resize', () => { for (const state of states.values()) state.schedule(); }, { passive: true });
})();
